import { Button, Col, Flex, Row, Tabs, TabsProps } from 'antd';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// components
import Icon from 'utils/icon';

// styles
import styles from '../pages/dashboard.module.scss';

// models
import { useSelector } from 'react-redux';
import { RootState } from 'stores/store';
import AccountActivationModal from 'components/activationModal/activationModal';
import SubscriptionContractComponent from 'modules/main/companyDocuments/components/subscriptionContract';
import UserAssignmentComponent from 'modules/main/companyDocuments/components/userAssignments';
import SignatoryPersonComponent from 'modules/main/companyDocuments/components/signatoryPerson';

interface InactiveAccountComponentProps {
	onActivateFinish: () => void;
}

const InactiveAccountComponent: FunctionComponent<InactiveAccountComponentProps> = ({ onActivateFinish }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const company = useSelector((state: RootState) => state.company);

	const [activateModalOpen, setActivateModalOpen] = useState<boolean>(false);

	let tabItems: TabsProps['items'] = [
		{
			key: 'contract',
			label: t('DOCUMENTS.SUBSCRIPTION_CONTRACT'),
			destroyInactiveTabPane: true,
			children: <SubscriptionContractComponent />,
		},
		{
			key: 'user_assignment',
			label: t('DOCUMENTS.USER_ASSIGNMENT_FORM'),
			destroyInactiveTabPane: true,
			children: <UserAssignmentComponent />,
		},
	];

	if (
		company?.registration_step === 'document_downloads' ||
		(company?.profile_status === 'FieldUpdateRequest' && company?.field_update_request)
	) {
		if (company?.field_update_request?.includes('certified_contract') || !company?.has_documents)
			tabItems.push({
				key: 'certified_contract',
				label: t('DOCUMENTS.AUTHORIZED_SIGNATORY_PERSON'),
				destroyInactiveTabPane: true,
				children: <SignatoryPersonComponent />,
			});
	}

	return (
		<>
			{/*
			 * If Account is Active return start using the system box
			 * If Account Rejected or Blocked return a rejection box
			 * If Account UnderReview and has documents and shareholders UnderReview return only Account UnderReview box
			 * Else return Shareholders and Documents boxes with their statuses
			 */}
			{company?.profile_status === 'Active' && company?.company_shareholder_status === 'Approved' ? (
				<Row className="mt-4" gutter={[16, 16]}>
					<Col span={24} xl={16} xxl={12}>
						<Flex align="center" justify="space-between" gap={20} className={`${styles.accountStatus} ${styles.Active}`}>
							<div>
								<h6 className="fw-semibold mb-3">{t('DASHBOARD.ACCOUNT_APPROVED')}</h6>
								<p className={`fw-semibold`}> {t('DASHBOARD.ACTIVATE_ACCOUNT')}</p>
								<Button
									size="middle"
									type="primary"
									style={{ backgroundColor: '#22b54b', color: '#fff' }}
									onClick={() => setActivateModalOpen(true)}>
									{t('COMMON.ACTIVATE')}
								</Button>
							</div>
							<Icon name="checkStep" width={100} height={125} style={{ minWidth: 100 }} />
						</Flex>
					</Col>
				</Row>
			) : company?.profile_status === 'Rejected' || company?.profile_status === 'Blocked' ? (
				<Row className="mt-4" gutter={[16, 16]}>
					<Col span={24} xl={16} xxl={12}>
						<Flex align="center" justify="space-between" gap={20} className={`${styles.accountStatus} ${styles.Blocked}`}>
							<div>
								<h6 className="fw-semibold mb-3">
									{company?.profile_status === 'Blocked' ? t('DASHBOARD.ACCOUNT_BLOCKED') : t('DASHBOARD.ACCOUNT_REJECTED')}
								</h6>
								<h6 className={`fw-semibold text-danger`}> {company?.profile_status_reason}</h6>
							</div>
							<Icon name="checkStep" width={100} height={125} style={{ minWidth: 100 }} />
						</Flex>
					</Col>
				</Row>
			) : company?.profile_status === 'UnderReview' && company?.has_documents && company?.company_shareholder_status === 'UnderReview' ? (
				<Row className="mt-4" gutter={[16, 16]}>
					<Col span={24} xl={16} xxl={12}>
						<Flex align="center" justify="space-between" gap={20} className={`${styles.accountStatus} ${styles.Waiting}`}>
							<div>
								<h6 className="fw-semibold">{t('DASHBOARD.ACCOUNT_UNDER_REVIEW')}</h6>
								<p className={`fw-semibold`}> {t('DASHBOARD.ACCOUNT_UNDER_REVIEW_DESC')}</p>
							</div>
							<Icon name="checkStep" width={100} height={125} style={{ minWidth: 100 }} />
						</Flex>
					</Col>
				</Row>
			) : (
				<>
					{/* SHAREHOLDERS NOTIFICATION */}
					<Row className="mt-4" gutter={[16, 16]}>
						<Col span={24} xl={16} xxl={12}>
							<Flex
								align="center"
								justify="space-between"
								gap={20}
								className={`${styles.accountStatus} ${styles[company?.company_shareholder_status!]}`}>
								<div>
									<h6 className="fw-semibold">
										{company?.company_shareholder_status == 'Waiting'
											? t('DASHBOARD.PLEASE_ADD_SHAREHOLDERS')
											: company?.company_shareholder_status == 'UnderReview'
											? t('DASHBOARD.SHAREHOLDERS_UPLOADED')
											: company?.company_shareholder_status == 'Rejected'
											? t('DASHBOARD.SHAREHOLDERS_REJECTED')
											: company?.company_shareholder_status == 'Approved'
											? t('DASHBOARD.SHAREHOLDERS_APPROVED')
											: ''}
									</h6>
									<p className={`fw-semibold ${company?.company_shareholder_status == 'Rejected' ? 'text-danger' : ''}`}>
										{company?.company_shareholder_status == 'Waiting' ? t('DASHBOARD.YOU_CAN_UPLOAD_SHAREHOLDERS') : ''}
									</p>

									<Button type="link" className="p-0 fw-medium" size="middle" onClick={() => navigate('/dashboard/shareholders-registry')}>
										<Icon name="users" width={15} height={15} />
										{company?.company_shareholder_status == 'Waiting'
											? t('SIDEBAR.ADD_SHAREHOLDERS_REGISTRY')
											: company?.company_shareholder_status == 'UnderReview' || company?.company_shareholder_status == 'Approved'
											? t('SIDEBAR.VIEW_SHAREHOLDERS_REGISTRY')
											: company?.company_shareholder_status == 'Rejected'
											? t('SIDEBAR.UPDATE_SHAREHOLDERS_REGISTRY')
											: ''}
									</Button>
								</div>
								<Icon name="checkStep" width={100} height={125} style={{ minWidth: 100 }} />
							</Flex>
						</Col>
					</Row>

					{/* DOCUMENTS NOTIFICATION */}
					<Row className="mt-4" gutter={[16, 16]}>
						<Col span={24} xl={16} xxl={12}>
							<Flex
								align="center"
								justify="space-between"
								gap={20}
								className={`${styles.accountStatus} ${
									company?.profile_status === 'FieldUpdateRequest'
										? styles.Rejected
										: company?.has_documents
										? styles.UnderReview
										: styles.Waiting
								}
						`}>
								<div>
									<h6 className="fw-semibold">
										{!company?.has_documents
											? t('DASHBOARD.PLEASE_UPLOAD_DOCUMENTS')
											: company?.profile_status === 'FieldUpdateRequest'
											? t('DASHBOARD.DOCUMENTS_REJECTED')
											: t('DASHBOARD.DOCUMENTS_UPLOADED')}
									</h6>
									<p className={`fw-semibold ${company?.profile_status === 'FieldUpdateRequest' ? 'text-danger' : ''}`}>
										{company?.profile_status === 'FieldUpdateRequest' || !company?.has_documents
											? t('DASHBOARD.YOU_CAN_UPLOAD_DOCUMENTS')
											: ''}
									</p>
									<Button type="link" className="p-0 fw-medium" size="middle" onClick={() => navigate('/dashboard/company-documents')}>
										<Icon name="files" width={15} height={15} />
										{!company?.has_documents
											? t('SIDEBAR.ADD_COMPANY_DOCUMENTS')
											: company?.profile_status === 'FieldUpdateRequest'
											? t('SIDEBAR.UPDATE_COMPANY_DOCUMENTS')
											: t('SIDEBAR.VIEW_COMPANY_DOCUMENTS')}
									</Button>
								</div>
								<Icon name="checkStep" width={100} height={125} style={{ minWidth: 100 }} />
							</Flex>
						</Col>
					</Row>
				</>
			)}

			<Tabs defaultActiveKey="1" items={tabItems} size="large" className="mt-4" />

			<AccountActivationModal onActivateFinish={onActivateFinish} onClose={() => setActivateModalOpen(false)} open={activateModalOpen} />
		</>
	);
};

export default InactiveAccountComponent;
